import { useState, useRef, useEffect, useCallback } from 'react'
import { useApp } from '@/context/AppContext'
import AppHeader from '@/components/layout/AppHeader'
import Button from '@/components/common/Button'
import MarkdownText from '@/components/common/MarkdownText'
import { getToken } from '@/utils/auth'
import VoiceAgent from '@/components/VoiceAgent'

// ── CPS investigation stages ──────────────────────────────────────────────────
const STAGES = [
  { id: 'intake',      label: 'Intake',           emoji: '📋', desc: 'Report received by CPS' },
  { id: 'assigned',   label: 'Caseworker Assigned',emoji: '👤', desc: 'Caseworker contacts you' },
  { id: 'home-visit', label: 'Home Visit',         emoji: '🏠', desc: 'In-person assessment' },
  { id: 'assessment', label: 'Safety Assessment',  emoji: '🔍', desc: 'Safety determination made' },
  { id: 'open',       label: 'Case Opened',        emoji: '📂', desc: 'Formal case initiated' },
  { id: 'services',   label: 'Service Plan',       emoji: '📝', desc: 'Required services identified' },
  { id: 'monitoring', label: 'Monitoring',         emoji: '👁️', desc: 'Ongoing visits & compliance' },
  { id: 'closed',     label: 'Case Closed',        emoji: '✅', desc: 'Investigation complete' },
]

// ── Rights checklist ──────────────────────────────────────────────────────────
const RIGHTS = [
  { id: 'r1', text: 'Right to know the general nature of the allegations' },
  { id: 'r2', text: 'Right to have an attorney present during interviews' },
  { id: 'r3', text: 'Right to refuse entry without a court order (varies by state)' },
  { id: 'r4', text: 'Right to have a support person present' },
  { id: 'r5', text: 'Right to record conversations (one-party consent states)' },
  { id: 'r6', text: 'Right to appeal CPS findings' },
]

// ── CPS AI chat ───────────────────────────────────────────────────────────────
async function askCpsAdvisor(messages) {
  const IS_DEV = import.meta.env.DEV
  const endpoint = IS_DEV ? null : '/api/cps-advisor'

  if (IS_DEV) {
    // Dev fallback
    await new Promise(r => setTimeout(r, 1000))
    return "I'm here to help you navigate this CPS situation. First — take a breath. Many CPS investigations are closed quickly with no findings. Can you tell me which stage the investigation is in and what state you're in? That will help me give you the most accurate guidance."
  }

  const token = getToken()
  const res = await fetch(endpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({ messages }),
  })
  if (!res.ok) {
    let msg = 'Failed to reach CPS Advisor'
    try { const err = await res.json(); msg = err.error || msg } catch { /* ignore */ }
    throw new Error(msg)
  }
  const data = await res.json()
  return data.reply
}

// ── Stage progress bar ────────────────────────────────────────────────────────
function StageTracker({ activeStage, onSelect }) {
  const activeIdx = STAGES.findIndex(s => s.id === activeStage)
  return (
    <div className="flex flex-col gap-2">
      {STAGES.map((stage, i) => (
        <button
          key={stage.id}
          onClick={() => onSelect(stage.id)}
          className={[
            'flex items-center gap-3 p-3 rounded-xl border-2 text-left transition-all duration-150',
            activeStage === stage.id
              ? 'border-brand-500 bg-brand-50'
              : i < activeIdx
              ? 'border-green-200 bg-green-50'
              : 'border-slate-200 bg-white opacity-60',
          ].join(' ')}
        >
          <span className="text-lg flex-shrink-0">{i < activeIdx ? '✅' : stage.emoji}</span>
          <div className="flex-1 min-w-0">
            <p className={`text-sm font-semibold ${activeStage === stage.id ? 'text-brand-700' : i < activeIdx ? 'text-green-700' : 'text-slate-500'}`}>
              {stage.label}
            </p>
            <p className="text-sm text-slate-500 truncate">{stage.desc}</p>
          </div>
          {activeStage === stage.id && (
            <span className="text-xs font-bold text-brand-600 bg-brand-100 px-2 py-0.5 rounded-full flex-shrink-0">Current</span>
          )}
        </button>
      ))}
    </div>
  )
}

const CPS_GREETING = {
  id: 'cps-greeting',
  role: 'ai',
  text: "👋 I'm your CPS Advisor. I'm here to help you understand your rights and navigate this process with confidence.\n\nTap on your current stage in the Tracker tab, then come back here with any questions.",
}

// ── Main screen ───────────────────────────────────────────────────────────────
export default function CpsTracker() {
  const { user, cpsHistory, appendCpsMessage } = useApp()
  const [tab, setTab] = useState('tracker')       // tracker | chat | rights
  const [activeStage, setActiveStage] = useState('intake')
  const [messages, setMessages] = useState(
    cpsHistory.length > 0 ? cpsHistory : [CPS_GREETING]
  )
  const [input, setInput] = useState('')
  const [isTyping, setIsTyping] = useState(false)
  const [checkedRights, setCheckedRights] = useState([])
  const bottomRef = useRef(null)
  const lastQueryRef = useRef('')   // stores last user message for retry

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }) }, [messages, isTyping])

  const callAdvisor = useCallback(async (text, conversationMessages) => {
    setIsTyping(true)
    try {
      const apiMessages = conversationMessages
        .filter(m => m.id !== 'cps-greeting' && !m._error)
        .map(m => ({ role: m.role === 'ai' ? 'assistant' : m.role, content: m.text }))
      const reply = await askCpsAdvisor(apiMessages)
      const aiMsg = { id: Date.now() + 1, role: 'ai', text: reply }
      setMessages(prev => [...prev.filter(m => !m._error), aiMsg])
      appendCpsMessage(aiMsg)
    } catch {
      const errMsg = {
        id: Date.now() + 1,
        role: 'ai',
        text: '⚠️ I had trouble connecting to the advisor. This may be a temporary issue — please try again.',
        _error: true,
        _retryText: text,
      }
      setMessages(prev => [...prev.filter(m => !m._error), errMsg])
    } finally {
      setIsTyping(false)
    }
  }, [appendCpsMessage])

  const send = async (overrideText) => {
    const trimmed = (overrideText || input).trim()
    if (!trimmed || isTyping) return
    if (!overrideText) setInput('')

    lastQueryRef.current = trimmed
    const userMsg = { id: Date.now(), role: 'user', text: trimmed }
    // Strip any stale error message before appending the new user message
    const updatedMessages = [...messages.filter(m => !m._error), userMsg]
    setMessages(updatedMessages)
    appendCpsMessage(userMsg)

    await callAdvisor(trimmed, updatedMessages)
  }

  const retry = (retryText) => {
    // Re-call the advisor with the existing conversation (error already stripped by callAdvisor)
    const cleanMessages = messages.filter(m => !m._error)
    setMessages(cleanMessages)
    callAdvisor(retryText, cleanMessages)
  }

  const currentStage = STAGES.find(s => s.id === activeStage)

  return (
    <div className="flex flex-col h-full bg-white">
      <AppHeader title="CPS Case Tracker" showBack />

      {/* State badge */}
      {user?.state && (
        <div className="px-4 py-2 bg-red-50 border-b border-red-100 flex items-center gap-2">
          <span className="text-xs text-red-600">🛡️ CPS rights guidance for</span>
          <span className="text-xs font-bold text-red-700">{user.state}</span>
        </div>
      )}

      {/* Tab bar */}
      <div className="flex border-b border-slate-100 flex-shrink-0">
        {[['tracker','📍 Tracker'], ['chat','🤖 AI Advisor'], ['rights','⚖️ My Rights']].map(([id, label]) => (
          <button key={id} onClick={() => setTab(id)}
            className={`flex-1 py-3 text-xs font-semibold transition-colors ${tab === id ? 'text-brand-700 border-b-2 border-brand-700' : 'text-slate-500'}`}>
            {label}
          </button>
        ))}
      </div>

      {/* Tracker tab */}
      {tab === 'tracker' && (
        <div className="screen no-scrollbar px-4 py-4 flex flex-col gap-4">
          <div className="card bg-brand-50 border border-brand-200">
            <p className="text-xs font-bold text-brand-700 mb-1">Current Stage</p>
            <div className="flex items-center gap-2">
              <span className="text-2xl">{currentStage?.emoji}</span>
              <div>
                <p className="font-bold text-slate-800">{currentStage?.label}</p>
                <p className="text-xs text-slate-500">{currentStage?.desc}</p>
              </div>
            </div>
          </div>
          <p className="text-xs text-slate-500 font-medium">Tap your current stage to update:</p>
          <StageTracker activeStage={activeStage} onSelect={setActiveStage} />
          <Button variant="primary" size="md" fullWidth onClick={() => setTab('chat')}>
            Ask CPS Advisor About This Stage →
          </Button>
          <div className="disclaimer">
            ⚠️ CPS processes vary significantly by state. Always consult a family law attorney if a case is opened against you.
          </div>
        </div>
      )}

      {/* Chat tab */}
      {tab === 'chat' && (
        <div className="flex flex-col flex-1 min-h-0">
          <div className="flex-1 overflow-y-auto no-scrollbar px-4 py-4 flex flex-col gap-3">
            {messages.map((m, i) => (
              <div key={m.id || i} className={`flex ${m.role === 'user' ? 'justify-end' : 'items-end gap-2'}`}>
                {m.role === 'ai' && (
                  <div className="w-7 h-7 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0 mb-1 text-xs">🛡️</div>
                )}
                <div className="flex flex-col gap-2">
                  <div className={m.role === 'user' ? 'bubble-user' : 'bubble-ai'}>
                    <MarkdownText content={m.text} isUser={m.role === 'user'} />
                  </div>
                  {m._error && m._retryText && (
                    <button
                      onClick={() => retry(m._retryText)}
                      className="self-start ml-1 inline-flex items-center gap-1.5 text-xs font-semibold text-brand-700 border border-brand-300 bg-brand-50 hover:bg-brand-100 active:scale-95 px-3 py-1.5 rounded-lg transition-all"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                      Try Again
                    </button>
                  )}
                </div>
              </div>
            ))}
            {isTyping && (
              <div className="flex items-end gap-2">
                <div className="w-7 h-7 rounded-full bg-red-100 flex items-center justify-center text-xs flex-shrink-0">🛡️</div>
                <div className="bubble-ai flex gap-1 py-4 px-5">
                  <span className="typing-dot w-1.5 h-1.5 rounded-full bg-slate-400 inline-block" />
                  <span className="typing-dot w-1.5 h-1.5 rounded-full bg-slate-400 inline-block" />
                  <span className="typing-dot w-1.5 h-1.5 rounded-full bg-slate-400 inline-block" />
                </div>
              </div>
            )}
            <div ref={bottomRef} />
          </div>
          <div className="flex-shrink-0 border-t border-slate-100 px-4 py-3 flex items-end gap-3 bg-white">
            <textarea value={input} onChange={e => setInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send() } }}
              placeholder="Ask about your CPS situation..."
              rows={1} className="flex-1 input-base resize-none text-base py-3 max-h-36" style={{ minHeight: 50 }} />
            <VoiceAgent
              agent="cps-advisor"
              onTranscript={(text, role) => {
                setMessages(m => [...m, { role: role === 'user' ? 'user' : 'assistant', content: text }])
              }}
            />
            <button onClick={() => send()} disabled={!input.trim() || isTyping}
              className={`flex-shrink-0 w-11 h-11 rounded-xl flex items-center justify-center transition-all ${input.trim() && !isTyping ? 'bg-red-600 text-white shadow-md active:scale-95' : 'bg-slate-200 text-slate-500 cursor-not-allowed'}`}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} className="w-5 h-5 rotate-90">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 19V5m0 0l-7 7m7-7l7 7" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Rights tab */}
      {tab === 'rights' && (
        <div className="screen no-scrollbar px-4 py-4 flex flex-col gap-4">
          <div>
            <h2 className="font-bold text-slate-800">Your Rights During a CPS Investigation</h2>
            <p className="text-xs text-slate-500 mt-1">Tap each right to mark it as reviewed.</p>
          </div>
          <div className="flex flex-col gap-2">
            {RIGHTS.map(r => (
              <button key={r.id} onClick={() => setCheckedRights(p => p.includes(r.id) ? p.filter(x => x !== r.id) : [...p, r.id])}
                className={`flex items-start gap-3 p-3 rounded-xl border-2 text-left transition-all ${checkedRights.includes(r.id) ? 'border-green-300 bg-green-50' : 'border-slate-200 bg-white'}`}>
                <div className={`w-5 h-5 rounded-full flex-shrink-0 flex items-center justify-center mt-0.5 transition-all ${checkedRights.includes(r.id) ? 'bg-green-500' : 'border-2 border-slate-300'}`}>
                  {checkedRights.includes(r.id) && <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth={3} className="w-3 h-3"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>}
                </div>
                <p className="text-sm text-slate-700 leading-relaxed">{r.text}</p>
              </button>
            ))}
          </div>
          <div className="disclaimer">
            ⚠️ Rights vary by state. Some states have additional protections. Consult a family law attorney to understand your specific rights in {user?.state || 'your state'}.
          </div>
        </div>
      )}
    </div>
  )
}
